import React from 'react';

import {useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';

import {generatePath, useParams} from 'react-router-dom';

import {ReactComponent as MinusIcon} from 'assets/svg/minus.svg';

import {ReactComponent as SettingsIcon} from 'assets/svg/options.svg';
import {BackTo} from 'components/common/BackTo';
import {Page} from 'components/common/Page';
import {PermissionAccess} from 'components/common/PermissionAccess';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Icon} from 'components/ui-kit/Icon';
import {Modal} from 'components/ui-kit/Modal';
import {ContentContainer} from 'components/ui-kit/PageComponents';
import {history} from 'core/history';
import {ROUTE} from 'core/routes';
import {
    customs$,
    customsUserIdsForUnboundConfirmation$,
    onChangeCustomsUserIdForUnboundConfirmation,
    onSetEditUser,
    unboundCustomsUserFx,
} from 'models/customs/users/table';
import {UserPermission} from 'types';
import {CustomsUserRow, StaffAccessors as Accessors} from 'types/profile';
import {handleNil} from 'utils/handleNil';

import {
    Wrapper,
    Action,
    ActionsContainer,
    CancelConfirmButton,
    ConfirmRemoveFooter,
    ControlsWrapper,
    HeaderControls,
    SubmitConfirmButton,
    Title,
} from './styled';

import {CustomsUsersGridTable} from './Table';
import {TableSearch} from './Table/SearchFilter';

export const convertData = (id: string) => (data: CustomsUserRow[] | undefined) => {
    if (!data) return [];

    return data.map((elem) => ({
        fullName: handleNil(
            `${elem.firstName ?? ''} ${elem.middleName ?? ''} ${elem.lastName ?? ''}`.trim(),
        ),
        email: handleNil(elem.email),
        phoneNumber: handleNil(elem.phone),
        actions: handleNil(elem, (value: CustomsUserRow) => (
            <ActionsContainer>
                <Action
                    disabled={value.customs.length <= 1}
                    onClick={() =>
                        onChangeCustomsUserIdForUnboundConfirmation({
                            customsId: id,
                            userId: value.userId,
                        })
                    }
                >
                    <Icon source={MinusIcon} fillColor="#F54848" width={18} height={18} />
                </Action>
                <Action
                    onClick={() => {
                        onSetEditUser(value);
                        history.push(generatePath(ROUTE.CUSTOMS_ID_USER_UPDATE, {id}));
                    }}
                >
                    <Icon source={SettingsIcon} fillColor="#000" width={18} height={18} />
                </Action>
            </ActionsContainer>
        )),
    }));
};

const getConfig = (t: TFunction, permissions?: UserPermission[]) => [
    {
        id: 'fullName',
        header: t('pages.customsUsers.fields.fullName'),
    },
    {
        id: 'email',
        header: t('pages.customsUsers.fields.email'),
    },
    {
        id: 'phoneNumber',
        header: t('pages.customsUsers.fields.phoneNumber'),
        width: 300,
    },
    ...(permissions?.includes(UserPermission.CUSTOMS_EMPLOYEE_EDIT)
        ? [
              {
                  id: Accessors.actions,
                  header: t('pages.customsUsers.fields.actions'),
                  width: 60,
              },
          ]
        : []),
];

export const CustomsUsersByIdPage = () => {
    const {t} = useTranslation();
    const params = useParams<{id: string}>();
    const ids = useStore(customsUserIdsForUnboundConfirmation$);

    const customs = useStore(customs$);

    const headerControls = (
        <HeaderControls>
            <Title>{customs?.name}</Title>
            <ControlsWrapper>
                <TableSearch />
                <PermissionAccess permissions={[UserPermission.CUSTOMS_EMPLOYEE_CREATE]}>
                    <Button
                        onClick={() =>
                            history.push(
                                generatePath(ROUTE.CUSTOMS_ID_USER_CREATE, {id: params.id}),
                            )
                        }
                    >
                        {t('pages.customsUsers.buttons.create')}
                    </Button>
                </PermissionAccess>
            </ControlsWrapper>
        </HeaderControls>
    );

    const ConfirmUnboundUser = () => {
        const onUnboundUser = async () => {
            if (!ids) return;
            await unboundCustomsUserFx(ids);
            onChangeCustomsUserIdForUnboundConfirmation(null);
        };
        const translate = (key: string) => t('pages.customsUsers.confirm.' + key);
        return (
            <Modal modalWidth={0} show={!!ids} hideCloseButton onClose={() => {}}>
                <p style={{textAlign: 'center'}}>{translate('message')}</p>
                <ConfirmRemoveFooter>
                    <CancelConfirmButton
                        variant={ButtonVariant.Outline}
                        onClick={() => onChangeCustomsUserIdForUnboundConfirmation(null)}
                    >
                        {translate('cancel')}
                    </CancelConfirmButton>
                    <SubmitConfirmButton onClick={onUnboundUser}>
                        {translate('ok')}
                    </SubmitConfirmButton>
                </ConfirmRemoveFooter>
            </Modal>
        );
    };

    return (
        <Page title={t('pages.profile.title')}>
            <Wrapper>
                <BackTo to={ROUTE.CUSTOMS}>{t('pages.customs.buttons.back')}</BackTo>
                <ContentContainer noPaddings>
                    <CustomsUsersGridTable
                        configFunc={getConfig}
                        dataMapper={convertData(params.id)}
                        headerControls={headerControls}
                    />
                </ContentContainer>
            </Wrapper>
            {ids && <ConfirmUnboundUser />}
        </Page>
    );
};
